import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { FEATURES } from "@/config/features";
import { LanguageSwitcher } from "@/components/marketing/LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { marketingLocaleForPath } from "@/config/locales";

const enNavLinks = [
  ...(FEATURES.FREE_SCAN_ENABLED ? [{ label: "Free Audit", to: "/scan" }] : []),
  { label: "How It Works", to: "/how-it-works" },
  { label: "Product Demo", to: "/demo" },
  { label: "Insights", to: "/insights" },
  { label: "Academy", to: "/academy" },
  { label: "Pricing", to: "/pricing" },
];

/** Spanish pages: only links with an existing Spanish destination are localized. */
const esNavLinks = enNavLinks.map((l) =>
  l.to === "/scan"
    ? { label: "Auditoría gratuita", to: "/es/free-ai-visibility-scan" }
    : l.to === "/pricing"
      ? { label: "Precios", to: "/es/pricing" }
      : l,
);

export function PublicNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const navLinks = marketingLocaleForPath(location.pathname) === "es" ? esNavLinks : enNavLinks;
  // Public pages can still carry a signed-in session. Offer Dashboard and the
  // existing sign-out; the account email is never shown in the public header.
  const { user, signOut } = useAuth();
  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="container flex h-14 items-center justify-between">
        <Link to="/" className="font-heading text-lg font-semibold tracking-tight text-foreground">
          Authority<span className="text-signal">Layer</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm transition-colors duration-200 ${
                location.pathname === link.to
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher className="hidden md:flex" />
          {user ? (
            <>
              <Button variant="ghost" size="sm" asChild className="hidden md:inline-flex text-foreground">
                <Link to="/app/overview">Dashboard</Link>
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="hidden md:inline-flex">
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild className="hidden md:inline-flex text-foreground">
                <Link to="/auth/sign-in">Sign In</Link>
              </Button>
              <Button variant="signal" size="sm" asChild className="hidden md:inline-flex">
                <Link to="/auth/sign-up?next=/pricing">Sign Up</Link>
              </Button>
            </>
          )}

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="font-heading text-lg">
                  Authority<span className="text-signal">Layer</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={`px-3 py-2 rounded-md text-sm transition-colors ${
                      location.pathname === link.to
                        ? "bg-muted text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-border my-3" />
                {user ? (
                  <>
                    <Link to="/app/overview" onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50">
                      Dashboard
                    </Link>
                    <Button variant="outline" size="sm" onClick={handleSignOut} className="mx-3 mt-1">
                      Sign out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/auth/sign-in" onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50">
                      Sign In
                    </Link>
                    <Button variant="signal" size="sm" asChild className="mx-3 mt-1">
                      <Link to="/auth/sign-up?next=/pricing" onClick={() => setOpen(false)}>Sign Up</Link>
                    </Button>
                  </>
                )}
                <LanguageSwitcher className="mt-4 px-3" />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
